/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';

import * as fromAccess from './access.reducer';
import * as fromHeaderNotifications from './header-notifications.reducer';
import * as fromLoginModal from './login-modal.reducer';
import * as fromPurchase from './purchase.reducer';
import * as fromSearchData from './search.reducer';
import * as fromSignup from './signup.reducer';
import * as fromSports from './sports.reducer';
import * as fromStickyMenu from './sticky-menu.reducer';
import * as fromSystemNotification from './system-notification.reducer';
import * as fromTipModal from './tip-modal.reducer';
import * as fromTeamSelectionModal from './team-selection-modal.reducer';
import * as fromUserData from './user-data.reducer';
import * as fromUserIdle from './user-idle.reducer';
import * as fromUserInvites from './user-invites.reducer';
import * as fromUserNewVideos from './user-new-videos.reducer';

export interface SharedState {
  access: fromAccess.AccessState;
  headerNotificationsState: fromHeaderNotifications.GetHeaderNotificationsState;
  loginModalData: fromLoginModal.LoginModalState;
  purchase: fromPurchase.PurchaseState;
  searchData: fromSearchData.SearchState;
  signup: fromSignup.SignupState;
  sportsData: fromSports.SportsState;
  stickyMenu: fromStickyMenu.StickyMenuState;
  systemNotificationData: fromSystemNotification.SystemNotificationState;
  tipModalData: fromTipModal.TipModalState;
  teamSelectionData: fromTeamSelectionModal.TeamSelectionModalState;
  userData: fromUserData.UserDataState;
  userIdleData: fromUserIdle.UserIdleState;
  userInvites: fromUserInvites.UserInvitesState;
  userNewVideos: fromUserNewVideos.GetUserNewVideosState;
}

export const reducers: ActionReducerMap<SharedState> = {
  access: fromAccess.reducer,
  headerNotificationsState: fromHeaderNotifications.reducer,
  loginModalData: fromLoginModal.reducer,
  tipModalData: fromTipModal.reducer,
  teamSelectionData: fromTeamSelectionModal.reducer,
  searchData: fromSearchData.reducer,
  signup: fromSignup.reducer,
  sportsData: fromSports.reducer,
  stickyMenu: fromStickyMenu.reducer,
  systemNotificationData: fromSystemNotification.reducer,
  userData: fromUserData.reducer,
  userIdleData: fromUserIdle.reducer,
  userInvites: fromUserInvites.reducer,
  purchase: fromPurchase.reducer,
  userNewVideos: fromUserNewVideos.reducer,
};

export const getSharedState = createFeatureSelector<SharedState>('shared');

// user data state
export const getUserData = createSelector(getSharedState, (state: SharedState) => state.userData);
export const getUser = createSelector(getUserData, fromUserData.getUserData);
export const getUserLoading = createSelector(getUserData, fromUserData.getUserDataLoading);
export const getUserSuccess = createSelector(getUserData, fromUserData.getUserDataSuccess);
export const getUserFail = createSelector(getUserData, fromUserData.getUserDataFail);

// user invites state
export const getUserInvitesState = createSelector(getSharedState, (state: SharedState) => state.userInvites);

// sports state
export const getSportsData = createSelector(getSharedState, (state: SharedState) => state.sportsData);
export const getSports = createSelector(getSportsData, fromSports.getSports);

// search state
export const getSearchData = createSelector(getSharedState, (state: SharedState) => state.searchData);
export const getSearchTerm = createSelector(getSearchData, fromSearchData.getSearchTerm);

// signup state
export const getSignupState = createSelector(getSharedState, (state: SharedState) => state.signup);
export const getSignupFailed = createSelector(getSignupState, fromSignup.getSignupFailed);
export const getSignupLoading = createSelector(getSignupState, fromSignup.getSignupLoading);
export const getSignupSuccess = createSelector(getSignupState, fromSignup.getSignupSuccess);
export const getUserUpdateSuccess = createSelector(getSignupState, fromSignup.getUserUpdateSuccess);
export const getUserPlan = createSelector(getSignupState, fromSignup.getUserPlan);
export const getUserActive = createSelector(getSignupState, fromSignup.getUserActive);
export const getSignupUserData = createSelector(getSignupState, fromSignup.getUserData);

// sticky menu state
export const getStickyMenuPosition = createSelector(getSharedState, (state: SharedState) => state.stickyMenu);

// system notification idle state
export const getSystemNotificationState = createSelector(
  getSharedState,
  (state: SharedState) => state.systemNotificationData
);

export const getSystemNotificationData = createSelector(
  getSystemNotificationState,
  fromSystemNotification.getSystemNotificationState
);

// login modal state
export const getLoginModalState = createSelector(getSharedState, (state: SharedState) => state.loginModalData);

export const getLoginModalData = createSelector(getLoginModalState, fromLoginModal.getLoginModalState);

// tip modal state
export const getTopModalState = createSelector(getSharedState, (state: SharedState) => state.tipModalData);

export const getTipModalData = createSelector(getLoginModalState, fromTipModal.getTipModalState);

// team selection modal state
export const getTeamSelectionState = createSelector(getSharedState, (state: SharedState) => state.teamSelectionData);

export const getTeamSelectionData = createSelector(
  getTeamSelectionState,
  fromTeamSelectionModal.getTeamSelectionModalState
);

// user idle state
export const getVideoPlaying = createSelector(getSharedState, (state: SharedState) => state.userIdleData);

// purchase state
export const getPurchaseState = createSelector(getSharedState, (state: SharedState) => state.purchase);

// access state
export const getAccessState = createSelector(getSharedState, (state: SharedState) => state.access);

// header notifications state
export const getHeaderNotifications = createSelector(
  getSharedState,
  (state: SharedState) => state.headerNotificationsState
);
// user new videos state
export const getUserNewVideosState = createSelector(getSharedState, (state: SharedState) => state.userNewVideos);
